'use client';

import React, { useState } from 'react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import Link from 'next/link';
import {
  BookOpen,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Package,
  Camera,
  CreditCard,
  Truck,
  MessageSquare,
  Gavel,
  Dog,
  Star,
} from 'lucide-react';


interface Section {
  id: string;
  icon: React.ElementType;
  iconColor: string;
  iconBg: string;
  title: string;
  intro: string;
  rules: { type: 'allowed' | 'prohibited' | 'required' | 'info'; text: string }[];
}

const sections: Section[] = [
  {
    id: 'listings',
    icon: Package,
    iconColor: 'text-amber-400',
    iconBg: 'bg-amber-400/10',
    title: 'Listing Standards',
    intro: 'Every listing must accurately represent the item being sold. Misleading listings damage trust and will be removed.',
    rules: [
      { type: 'required', text: 'Title must clearly identify the item — brand, model, and condition where applicable.' },
      { type: 'required', text: 'Description must be written in English and include all relevant details: size, age, condition, any defects or wear.' },
      { type: 'required', text: 'Price must be in GBP and reflect the actual asking price. No bait-and-switch pricing.' },
      { type: 'required', text: 'Category must accurately match the item. Miscategorised listings will be moved or removed.' },
      { type: 'allowed', text: 'You may list items as "open to offers" and negotiate via the offer system.' },
      { type: 'allowed', text: 'Bundle listings are permitted — clearly list all items included.' },
      { type: 'prohibited', text: 'Do not copy descriptions from other listings or manufacturer websites without disclosure.' },
      { type: 'prohibited', text: 'Do not list items you do not own or have no intention of selling.' },
      { type: 'prohibited', text: 'Do not create duplicate listings for the same item.' },
    ],
  },
  {
    id: 'photos',
    icon: Camera,
    iconColor: 'text-blue-400',
    iconBg: 'bg-blue-400/10',
    title: 'Photo Requirements',
    intro: 'Good photos are the single biggest factor in a successful sale. Buyers need to see exactly what they\'re getting.',
    rules: [
      { type: 'required', text: 'At least one photo of the actual item is required. Stock images are not permitted as the primary photo.' },
      { type: 'required', text: 'Photos must be clear, well-lit, and in focus. Blurry or dark images will be flagged.' },
      { type: 'required', text: 'Any defects, damage, or significant wear must be photographed and disclosed.' },
      { type: 'allowed', text: 'You may include up to 10 photos per listing. Use all available slots for high-value items.' },
      { type: 'allowed', text: 'Supplementary stock images may be included alongside real photos to show scale or context.' },
      { type: 'prohibited', text: 'Do not use photos that include other people without their consent.' },
      { type: 'prohibited', text: 'Do not use photos that contain offensive, inappropriate, or unrelated content.' },
      { type: 'prohibited', text: 'Do not use watermarked images from other websites.' },
    ],
  },
  {
    id: 'prohibited',
    icon: XCircle,
    iconColor: 'text-rose-400',
    iconBg: 'bg-rose-400/10',
    title: 'Prohibited Items',
    intro: 'The following items are strictly prohibited on DrivenMarket. Listings will be removed immediately and may result in account suspension.',
    rules: [
      { type: 'prohibited', text: 'Stolen goods or items obtained through fraud.' },
      { type: 'prohibited', text: 'Counterfeit, replica, or fake branded items.' },
      { type: 'prohibited', text: 'Illegal weapons, prohibited knives, or items that require a firearms licence without appropriate verification.' },
      { type: 'prohibited', text: 'Items that violate UK animal welfare law, including equipment banned under the Animal Welfare Act 2006.' },
      { type: 'prohibited', text: 'Prong collars, shock collars, or aversive training tools where their sale is restricted by law.' },
      { type: 'prohibited', text: 'Live animals listed without compliance with Lucy\'s Law and relevant breeding regulations.' },
      { type: 'prohibited', text: 'Drugs, controlled substances, or paraphernalia.' },
      { type: 'prohibited', text: 'Hazardous materials or items that cannot be legally shipped within the UK.' },
      { type: 'prohibited', text: 'Digital goods or services outside of the dedicated Resources section.' },
    ],
  },
  {
    id: 'dogs',
    icon: Dog,
    iconColor: 'text-green-400',
    iconBg: 'bg-green-400/10',
    title: 'Dog & Puppy Listings',
    intro: 'Dog and puppy listings are subject to additional requirements under UK law. Non-compliance will result in immediate removal.',
    rules: [
      { type: 'required', text: 'All puppy listings must come directly from the breeder. Third-party dealers are prohibited under Lucy\'s Law (2020).' },
      { type: 'required', text: 'Breeders must confirm they are the original breeder of the litter at the time of listing.' },
      { type: 'required', text: 'Microchip number must be provided for all dogs over 8 weeks old (UK law).' },
      { type: 'required', text: 'Vaccination records, health tests, and KC registration details must be accurately disclosed.' },
      { type: 'required', text: 'Breed must be accurately stated. Misrepresenting a dog\'s breed is fraudulent.' },
      { type: 'required', text: 'Prohibited breeds under the Dangerous Dogs Act 1991 (Pit Bull Terrier, Japanese Tosa, Dogo Argentino, Fila Brasileiro) may not be listed.' },
      { type: 'allowed', text: 'Working line dogs (Malinois, GSD, Dutch Shepherd, etc.) may be listed with full working history and titles.' },
      { type: 'info', text: 'DrivenMarket displays a Lucy\'s Law compliance notice on all dog and puppy listings.' },
    ],
  },
  {
    id: 'payments',
    icon: CreditCard,
    iconColor: 'text-purple-400',
    iconBg: 'bg-purple-400/10',
    title: 'Payments & Fees',
    intro: 'All transactions must be completed through DrivenMarket\'s secure checkout. Off-platform payments are prohibited.',
    rules: [
      { type: 'required', text: 'Complete Stripe Connect onboarding before listing items for sale. Unverified sellers cannot receive payouts.' },
      { type: 'required', text: 'All sales must be processed through DrivenMarket checkout. Bank transfers, PayPal, and cash are not permitted.' },
      { type: 'info', text: 'DrivenMarket charges a platform fee on completed sales. The fee is displayed at the time of listing.' },
      { type: 'info', text: 'Payouts are released after the buyer confirms receipt or the 5-day auto-release window closes.' },
      { type: 'prohibited', text: 'Do not ask buyers to pay outside the platform. This voids buyer protection and may result in permanent suspension.' },
      { type: 'prohibited', text: 'Do not offer discounts in exchange for off-platform payment.' },
      { type: 'allowed', text: 'You may offer local collection. The transaction must still be completed through DrivenMarket checkout.' },
    ],
  },
  {
    id: 'shipping',
    icon: Truck,
    iconColor: 'text-cyan-400',
    iconBg: 'bg-cyan-400/10',
    title: 'Shipping & Fulfilment',
    intro: 'Prompt, safe shipping is essential to maintaining your seller reputation and avoiding disputes.',
    rules: [
      { type: 'required', text: 'Ship items within the timeframe stated in your listing (default: 3 business days).' },
      { type: 'required', text: 'Provide a valid tracking number as soon as the item is dispatched.' },
      { type: 'required', text: 'Package items securely to prevent damage in transit. Use appropriate packaging for the item type.' },
      { type: 'required', text: 'Keep proof of postage. This is essential evidence in the event of a dispute.' },
      { type: 'allowed', text: 'You may offer local collection as an alternative to shipping.' },
      { type: 'allowed', text: 'You may use any reputable UK courier (Royal Mail, DPD, Evri, Parcelforce, etc.).' },
      { type: 'prohibited', text: 'Do not ship items that have not been paid for in full.' },
      { type: 'prohibited', text: 'Do not misrepresent shipping costs or charge excessive handling fees.' },
    ],
  },
  {
    id: 'communication',
    icon: MessageSquare,
    iconColor: 'text-indigo-400',
    iconBg: 'bg-indigo-400/10',
    title: 'Communication Standards',
    intro: 'Professional, honest communication is expected from all sellers. Buyers rely on your responses to make informed decisions.',
    rules: [
      { type: 'required', text: 'Respond to buyer messages within 48 hours. Slow responses lead to disputes and negative reviews.' },
      { type: 'required', text: 'Answer questions honestly and completely. Do not withhold information that would affect a buyer\'s decision.' },
      { type: 'prohibited', text: 'Do not harass, threaten, or abuse buyers.' },
      { type: 'prohibited', text: 'Do not share personal contact details (phone, email, WhatsApp) before a transaction is complete.' },
      { type: 'prohibited', text: 'Do not solicit off-platform transactions through the messaging system.' },
      { type: 'prohibited', text: 'Do not use automated or scripted responses that mislead buyers.' },
    ],
  },
  {
    id: 'enforcement',
    icon: Gavel,
    iconColor: 'text-orange-400',
    iconBg: 'bg-orange-400/10',
    title: 'Enforcement & Consequences',
    intro: 'We take guideline violations seriously. Our enforcement actions are proportionate to the severity and frequency of violations.',
    rules: [
      { type: 'info', text: 'First violation: Warning issued and listing removed. Seller notified by email.' },
      { type: 'info', text: 'Repeated violations: Temporary account suspension (7–30 days depending on severity).' },
      { type: 'info', text: 'Serious violations (fraud, prohibited items, off-platform solicitation): Permanent account ban and potential referral to law enforcement.' },
      { type: 'info', text: 'All enforcement decisions can be appealed by emailing appeals@drivenmarket.co.uk within 14 days.' },
      { type: 'info', text: 'DrivenMarket reserves the right to withhold payouts pending investigation of reported violations.' },
    ],
  },
  {
    id: 'reviews',
    icon: Star,
    iconColor: 'text-yellow-400',
    iconBg: 'bg-yellow-400/10',
    title: 'Reviews & Reputation',
    intro: 'Your seller reputation is your most valuable asset on DrivenMarket. Reviews are permanent and public.',
    rules: [
      { type: 'info', text: 'Buyers can leave a review after a completed transaction. Reviews are verified — only buyers who purchased from you can review you.' },
      { type: 'prohibited', text: 'Do not attempt to manipulate reviews — including asking friends to leave fake reviews or offering incentives for positive reviews.' },
      { type: 'prohibited', text: 'Do not threaten or pressure buyers to change or remove negative reviews.' },
      { type: 'allowed', text: 'You may respond publicly to reviews. Keep responses professional and factual.' },
      { type: 'info', text: 'Sellers with a rating below 3.0 after 10+ reviews may have their listings restricted pending review.' },
    ],
  },
];

const ruleIcon = (type: string) => {
  switch (type) {
    case 'required': return <CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />;
    case 'allowed': return <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />;
    case 'prohibited': return <XCircle className="w-4 h-4 text-rose-400 flex-shrink-0 mt-0.5" />;
    case 'info': return <AlertTriangle className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />;
    default: return null;
  }
};

export default function GuidelinesPage() {
  const [openSection, setOpenSection] = useState<string | null>('listings');

  return (
    <div className="min-h-screen bg-[hsl(222,47%,6%)]">
      <Topbar />
      <main className="pt-16">
        {/* Hero */}
        <div className="bg-gradient-to-b from-[hsl(222,47%,8%)] to-[hsl(222,47%,6%)] border-b border-[hsl(222,25%,18%)]">
          <div className="max-w-4xl mx-auto px-4 lg:px-8 py-14 text-center">
            <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-amber-400/10 mb-5">
              <BookOpen className="w-7 h-7 text-amber-400" />
            </div>
            <h1 className="text-3xl font-bold text-white mb-3">Seller Guidelines</h1>
            <p className="text-slate-400 text-base leading-relaxed max-w-2xl mx-auto">
              These guidelines exist to protect buyers, sellers, and the integrity of the DrivenMarket community. All sellers must read and comply with these rules.
            </p>
            <p className="text-slate-500 text-xs mt-4">Last updated: 1 April 2026</p>
          </div>
        </div>

        <div className="max-w-4xl mx-auto px-4 lg:px-8 py-12">

          {/* Legend */}
          <div className="flex flex-wrap gap-3 mb-8 p-4 bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl">
            <span className="text-xs text-slate-500 font-semibold uppercase tracking-widest w-full mb-1">Key</span>
            <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-amber-400 flex-shrink-0" /><span className="text-slate-400 text-xs">Required</span></div>
            <div className="flex items-center gap-2"><CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" /><span className="text-slate-400 text-xs">Allowed</span></div>
            <div className="flex items-center gap-2"><XCircle className="w-4 h-4 text-rose-400 flex-shrink-0" /><span className="text-slate-400 text-xs">Prohibited</span></div>
            <div className="flex items-center gap-2"><AlertTriangle className="w-4 h-4 text-blue-400 flex-shrink-0" /><span className="text-slate-400 text-xs">Important Info</span></div>
          </div>

          {/* Sections */}
          <div className="space-y-3">
            {sections.map((section) => {
              const SectionIcon = section.icon;
              const isOpen = openSection === section.id;
              return (
                <div key={section.id} className="bg-[hsl(222,40%,9%)] border border-[hsl(222,25%,18%)] rounded-xl overflow-hidden">
                  <button
                    onClick={() => setOpenSection(isOpen ? null : section.id)}
                    className="w-full flex items-center justify-between gap-3 px-4 sm:px-5 py-4 text-left hover:bg-white/[0.02] transition-colors"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <div className={`w-9 h-9 rounded-lg ${section.iconBg} flex items-center justify-center flex-shrink-0`}>
                        <SectionIcon className={`w-4 h-4 ${section.iconColor}`} />
                      </div>
                      <span className="text-sm font-semibold text-white leading-snug">{section.title}</span>
                    </div>
                    {isOpen ? (
                      <ChevronUp className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-slate-400 flex-shrink-0" />
                    )}
                  </button>
                  {isOpen && (
                    <div className="border-t border-[hsl(222,25%,18%)] px-4 sm:px-5 py-5">
                      <p className="text-slate-400 text-sm leading-relaxed mb-5">{section.intro}</p>
                      <div className="space-y-3">
                        {section.rules.map((rule, i) => (
                          <div key={i} className="flex items-start gap-3">
                            {ruleIcon(rule.type)}
                            <p className="text-slate-300 text-sm leading-relaxed">{rule.text}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Footer CTA */}
          <div className="mt-10 bg-amber-400/5 border border-amber-400/20 rounded-xl p-5 sm:p-6 text-center">
            <h3 className="text-sm font-semibold text-white mb-2">Questions about these guidelines?</h3>
            <p className="text-slate-400 text-xs leading-relaxed mb-4">
              If you're unsure whether your listing or item complies with our guidelines, contact us before posting.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link href="/contact" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-amber-400 hover:bg-amber-300 text-black text-xs font-semibold rounded-lg transition-colors">
                Contact Support
              </Link>
              <Link href="/safety" className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white text-xs font-semibold rounded-lg transition-colors">
                Safety &amp; Trust
              </Link>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
